import { cn } from "@/lib/utils";

const VERDICT_STYLES = {
  "WORTH TESTING": "bg-donkey-green/15 text-donkey-green border-donkey-green/30",
  "PROMISING, BUT UNPROVEN": "bg-donkey-amber/15 text-donkey-amber border-donkey-amber/30",
  "NEEDS RETHINKING": "bg-donkey-amber/15 text-donkey-amber border-donkey-amber/30",
  "TOO EXPENSIVE TO TEST NOW": "bg-donkey-amber/15 text-donkey-amber border-donkey-amber/30",
  "POOR PERSONAL FIT": "bg-donkey-red/15 text-donkey-red border-donkey-red/30",
  "CUSTOMER ROUTE TOO WEAK": "bg-donkey-red/15 text-donkey-red border-donkey-red/30",
  "DON'T WASTE YOUR MONEY": "bg-donkey-red/15 text-donkey-red border-donkey-red/30",
  // legacy
  "Strong case": "bg-donkey-green/15 text-donkey-green border-donkey-green/30",
  "Promising but unproven": "bg-donkey-amber/15 text-donkey-amber border-donkey-amber/30",
  "Test before investing": "bg-donkey-amber/15 text-donkey-amber border-donkey-amber/30",
  "Weak fit": "bg-donkey-red/15 text-donkey-red border-donkey-red/30",
  "Do not pursue yet": "bg-donkey-red/15 text-donkey-red border-donkey-red/30",
};

const CONF_STYLES = {
  High: "text-donkey-green border-donkey-green/40",
  Moderate: "text-donkey-amber border-donkey-amber/40",
  Low: "text-donkey-red border-donkey-red/40",
};

export function ConfidenceBadge({ level }) {
  if (!level) return null;
  return (
    <span className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[10px] font-medium ${CONF_STYLES[level] || "text-donkey-grey border-white/20"}`}>
      {level} confidence
    </span>
  );
}

export function EvidenceChip({ evidenceClass }) {
  if (!evidenceClass) return null;
  const label = String(evidenceClass).replace(/_/g, " ").toLowerCase();
  const strong = ["VERIFIED_USER_FACT", "PUBLIC_SOURCE_EVIDENCE", "CONNECTED_DATA"].includes(evidenceClass);
  const weak = ["ASSUMPTION", "UNKNOWN"].includes(evidenceClass);
  const cls = strong ? "text-donkey-green border-donkey-green/30"
    : weak ? "text-donkey-red border-donkey-red/30" : "text-donkey-grey border-white/20";
  return (
    <span className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[10px] font-mono ${cls}`} title="Evidence class">
      {label}
    </span>
  );
}

export function VerdictBadge({ label, className = "" }) {
  const style = VERDICT_STYLES[label] || "bg-white/10 text-donkey-warm border-white/20";
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold tracking-wide",
        style,
        className
      )}
      data-testid="verdict-badge"
    >
      {label}
    </span>
  );
}

function scoreColour(v) {
  if (v >= 70) return "#36B37E";
  if (v >= 45) return "#F5A623";
  return "#D9534F";
}

export function ScoreBar({ label, value, explanation }) {
  const v = Math.max(0, Math.min(100, Number(value) || 0));
  return (
    <div className="py-2.5" data-testid={`score-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-sm text-donkey-warm/90">{label}</span>
        <span className="font-mono text-sm" style={{ color: scoreColour(v) }}>{v}</span>
      </div>
      <div className="h-1.5 rounded-full bg-white/8 overflow-hidden">
        <div
          className="h-full rounded-full transition-all"
          style={{ width: `${v}%`, backgroundColor: scoreColour(v) }}
        />
      </div>
      {explanation && <p className="mt-1.5 text-xs text-donkey-grey leading-relaxed">{explanation}</p>}
    </div>
  );
}

export function ScoreRing({ value, size = 88 }) {
  const v = Math.max(0, Math.min(100, Number(value) || 0));
  const r = (size - 10) / 2;
  const c = 2 * Math.PI * r;
  const off = c - (v / 100) * c;
  const col = scoreColour(v);
  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} stroke="rgba(255,255,255,0.08)" strokeWidth="6" fill="none" />
        <circle
          cx={size / 2} cy={size / 2} r={r} stroke={col} strokeWidth="6" fill="none"
          strokeDasharray={c} strokeDashoffset={off} strokeLinecap="round"
          style={{ transition: "stroke-dashoffset 0.6s ease" }}
        />
      </svg>
      <div className="absolute flex flex-col items-center">
        <span className="font-mono font-semibold text-xl" style={{ color: col }}>{v}</span>
        <span className="text-[9px] uppercase tracking-widest text-donkey-grey">/100</span>
      </div>
    </div>
  );
}
